import {
    Button,
    ButtonStyles,
    EmptyStateIcons,
    EmptyStateProps,
    FloatingSnackbar,
    Icon,
    Icons,
    StatusType,
    Table,
    TextWithStatusIndicatorCell,
    Tooltip,
    TwoLinesCell
} from '@silverfort/sf-components';
import { FC, SVGProps, useCallback, useEffect, useMemo, useState } from 'react';
import { TableColumn } from '@silverfort/sf-components/dist/Table/interface';
import { AppUserProps, AppUserRowProps, Filter, OrderBy } from './Types';
import moment from 'moment/moment';
import { APP_USER_PERMISSIONS, PUBLIC_API_CATEGORIES } from '@silverfort-nodelibs/common/utils/consts';

const PERMISSIONS_BY_VALUE = Object.values(APP_USER_PERMISSIONS).reduce((acc, permission) => {
    acc[permission.value] = permission.label;
    return acc;
}, {});

const CATEGORIES_NAMES_BY_VALUE = Object.values(PUBLIC_API_CATEGORIES).reduce((acc, category) => {
    acc[category.value] = category.label;
    return acc;
}, {});

const BASE_COLUMNS: TableColumn<AppUserRowProps>[] = [
    {
        columnKey: 'name',
        title: 'API',
        render: ({ name, category, approachingExpiration }) => {
            return (
                <TwoLinesCell
                    topText={name}
                    bottomText={CATEGORIES_NAMES_BY_VALUE[category]}
                    rightElement={
                        approachingExpiration && (
                            <Tooltip title="API about to expire" className={'w-fit'}>
                                <Icon
                                    icon={Icons.Warning as FC<SVGProps<SVGSVGElement>>}
                                    size={20}
                                    className="text-sf-neutral-500"
                                />
                            </Tooltip>
                        )
                    }
                />
            );
        }
    },
    {
        columnKey: 'status',
        title: 'Status',
        render: ({ status }) => {
            let statusType: string;
            let text: string;
            switch (status) {
                case 'active':
                    statusType = StatusType.Default;
                    text = 'Active';
                    break;
                case 'expired':
                    statusType = StatusType.Error;
                    text = 'Expired';
                    break;
                default:
                    statusType = StatusType.Default;
                    text = status;
            }
            return <TextWithStatusIndicatorCell text={text} statusType={statusType as StatusType} />;
        }
    },
    { columnKey: 'appUserId', title: 'App user ID' },
    { columnKey: 'permissions', title: 'Permissions', render: ({ permissions }) => PERMISSIONS_BY_VALUE[permissions] },
    { columnKey: 'createdBy', title: 'Created by' },
    {
        columnKey: 'lastUsed',
        title: 'Last used',
        render: ({ lastUsed }) => (lastUsed ? moment(lastUsed).format('MMM DD, YYYY H:mm:ss') : 'N/A')
    }
];

interface AuthUsersTableProps {
    appUsers: AppUserProps[];
    filters: Filter[];
    searchValue: string;
    onCreate: () => void;
    onDelete: (appUserIds: string[]) => void;
    inactivityThreshold: number;
}

export const AuthUsersTable: FC<AuthUsersTableProps> = ({
    appUsers,
    filters,
    searchValue,
    onCreate,
    onDelete,
    inactivityThreshold
}) => {
    const [appUsersRows, setAppUsersRows] = useState<AppUserRowProps[]>([]);
    const [selectedAppUsers, setSelectedAppUsers] = useState<string[]>([]);

    const [orderBy, setOrderBy] = useState<OrderBy>({ columnKey: 'lastUsed', direction: 'desc' });
    const [emptyStateProps, setEmptyStateProps] = useState<EmptyStateProps>();

    const convertAppUserToRow = useCallback(
        (appUser: AppUserProps): AppUserRowProps => {
            let approachingExpiration = false;
            let status = 'active';
            if (appUser.last_login_time) {
                const lastUsedDays = moment(Date.now()).diff(moment(appUser.last_login_time), 'days');
                status = lastUsedDays > inactivityThreshold ? 'expired' : 'active';
                approachingExpiration = lastUsedDays >= 30;
            }
            return {
                name: appUser.name,
                category: appUser.category,
                status,
                approachingExpiration,
                appUserId: appUser.app_user_id,
                permissions: appUser.permissions,
                createdBy: appUser.created_by || 'N/A',
                lastUsed: appUser.last_login_time
            };
        },
        [inactivityThreshold]
    );

    const filterAndSortAppUsers = useCallback(() => {
        let rows = appUsers.map(convertAppUserToRow);
        const activeFilters = filters.filter((f) =>
            Array.isArray(f.selectedItems)
                ? f.selectedItems.length > 0 && f.selectedItems.length !== f.items.length
                : !!f.selectedItems
        );
        if (activeFilters.length) {
            rows = rows.filter((row: AppUserRowProps) =>
                activeFilters.every((filter: Filter) => {
                    const rowValue = row[filter.columnKey];
                    if (filter.type === 'text') {
                        return Array.isArray(filter.selectedItems)
                            ? (filter.selectedItems as string[]).includes(rowValue)
                            : filter.selectedItems === rowValue;
                    }
                    if (filter.type === 'days') {
                        const date = moment(rowValue).startOf('day');
                        const daysDiff = moment().startOf('day').diff(date, 'days');
                        return Array.isArray(filter.selectedItems)
                            ? (filter.selectedItems as number[]).includes(daysDiff)
                            : +filter.selectedItems >= daysDiff;
                    }
                    return true;
                })
            );
        }
        if (searchValue) {
            const searchValueLower = searchValue.toLowerCase();
            rows = rows.filter((row: AppUserRowProps) => row.name.toLowerCase().includes(searchValueLower));
        }
        if (orderBy) {
            rows.sort((a: AppUserRowProps, b: AppUserRowProps) => {
                const aValue = a[orderBy.columnKey];
                const bValue = b[orderBy.columnKey];
                if (!aValue && !bValue) return 0;
                if (!aValue) return orderBy.direction === 'asc' ? -1 : 1;
                if (!bValue) return orderBy.direction === 'asc' ? 1 : -1;
                if (aValue < bValue) return orderBy.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return orderBy.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return rows;
    }, [appUsers, convertAppUserToRow, filters, searchValue, orderBy]);

    useEffect(() => {
        if (!appUsers.length) {
            setEmptyStateProps({
                icon: EmptyStateIcons.NoItems as FC<SVGProps<SVGSVGElement>>,
                title: 'No API keys yet',
                description: 'Start by creating your first API key',
                actionIcon: Icons.Add as FC<SVGProps<SVGSVGElement>>,
                actionText: 'Create Key',
                onActionClick: onCreate
            });
            setAppUsersRows([]);
        } else {
            const rows = filterAndSortAppUsers();
            if (!rows.length) {
                setEmptyStateProps({
                    icon: EmptyStateIcons.NoResults as FC<SVGProps<SVGSVGElement>>,
                    title: 'No results found',
                    description: 'Try refining your search'
                });
            }
            setAppUsersRows(rows);
        }
        setSelectedAppUsers([]);
    }, [filters, orderBy, searchValue, appUsers, onCreate, filterAndSortAppUsers]);

    const keyExtractor = (item: AppUserRowProps) => `${item.appUserId}__${item.category}` as never;

    const ACTIONS_COL: TableColumn<AppUserRowProps> = {
        columnKey: '__actions' as keyof AppUserRowProps,
        title: '',
        disableSort: true,
        columnClassName: 'w-[56px] min-w-[56px] px-2 text-right',
        render: (row) => (
            <div className="flex justify-end">
                <Tooltip title="Delete API" placement="left">
                    <button
                        type="button"
                        aria-label="Delete API"
                        className="rounded-lg p-2 opacity-0 group-hover:opacity-100 focus:opacity-100
                     hover:bg-sf-gray-100 active:bg-sf-gray-200"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete([keyExtractor(row)]);
                        }}>
                        <Icon icon={Icons.Delete} size={16} className="text-sf-gray-600" />
                    </button>
                </Tooltip>
            </div>
        )
    };

    const COLUMNS: TableColumn<AppUserRowProps>[] = [...BASE_COLUMNS, ACTIONS_COL];

    return (
        <>
            <Table
                bodyHeight={500}
                columns={COLUMNS}
                rows={appUsersRows}
                keyExtractor={keyExtractor}
                selectable
                selected={selectedAppUsers}
                onSelect={setSelectedAppUsers}
                defaultSortColumn="lastUsed"
                defaultSortDirection="desc"
                EmptyStateProps={emptyStateProps}
                onSortChange={(columnKey, direction) => setOrderBy({ columnKey, direction })}
            />
            {!!selectedAppUsers.length && (
                <FloatingSnackbar
                    open={!!selectedAppUsers.length}
                    onClose={() => setSelectedAppUsers([])}
                    selections={selectedAppUsers.length}
                    selectionText={{ singular: 'API', plural: 'APIs' }}>
                    <Button
                        buttonStyle={ButtonStyles.Ghost}
                        iconLeft={Icons.Delete as FC<SVGProps<SVGSVGElement>>}
                        text="Delete"
                        onClick={() => onDelete(selectedAppUsers)}
                    />
                </FloatingSnackbar>
            )}
        </>
    );
};
